<template>
    <section v-if="variant === 'expanded'" class="mx-1 mt-5 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div class="border-b border-slate-100 px-3 pb-2.5 pt-3">
            <div class="flex items-start gap-2">
                <span class="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-md bg-blue-50 text-blue-700">
                    <UIcon name="i-ion-rocket-outline" class="h-3.5 w-3.5" />
                </span>
                <span class="min-w-0 flex-1">
                    <span class="block text-xs font-semibold text-slate-800">Set up outreach essentials</span>
                    <span class="mt-0.5 block text-[11px] leading-4 text-slate-500">Three essentials to complete before you send.</span>
                </span>
                <button type="button" aria-label="Collapse setup checklist" class="mt-0.5 text-slate-400 hover:text-slate-600" @click="expanded = false">
                    <UIcon name="i-ion-chevron-down" class="h-3.5 w-3.5" />
                </button>
            </div>
            <div class="mt-2 flex items-center gap-2">
                <div class="h-1 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div class="h-full rounded-full bg-blue-600 transition-all" :style="{ width: `${progressPercent}%` }" />
                </div>
                <span class="text-[10px] font-medium text-slate-500">{{ completedCount }} of {{ items.length }}</span>
            </div>
        </div>

        <div class="p-1.5">
            <NuxtLink
                v-for="item in items"
                :key="item.key"
                :to="item.to"
                class="group flex gap-2 rounded-md px-2 py-2 transition-colors"
                :class="item.complete ? 'text-slate-400' : 'hover:bg-slate-50'"
            >
                <span
                    class="mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full border"
                    :class="item.complete ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 bg-white text-slate-400'"
                >
                    <UIcon v-if="item.complete" name="i-ion-checkmark" class="h-3 w-3" />
                    <span v-else class="text-[9px] font-semibold leading-none">{{ item.step }}</span>
                </span>
                <span class="min-w-0 flex-1">
                    <span class="flex items-center gap-1 text-[11px] font-semibold leading-4" :class="item.complete ? 'text-slate-500' : 'text-slate-700 group-hover:text-blue-700'">
                        {{ item.label }}
                        <span v-if="item.complete" class="ml-auto text-[9px] font-medium uppercase tracking-wide text-emerald-600">Done</span>
                    </span>
                    <span v-if="!item.complete" class="mt-0.5 block text-[10px] leading-3.5 text-slate-500">{{ item.description }}</span>
                </span>
                <UIcon v-if="!item.complete" name="i-ion-chevron-forward" class="mt-1 h-3 w-3 flex-none text-slate-300 group-hover:text-blue-500" />
            </NuxtLink>
        </div>
    </section>

    <button
        v-else
        type="button"
        class="mx-1 mb-2 flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-2 text-left shadow-sm hover:border-blue-200 hover:bg-blue-50/40"
        @click="expanded = true"
    >
        <span class="relative flex h-5 w-5 flex-none items-center justify-center rounded-md bg-blue-50 text-blue-700">
            <UIcon name="i-ion-rocket-outline" class="h-3.5 w-3.5" />
        </span>
        <span class="min-w-0 flex-1">
            <span class="block text-[11px] font-semibold text-slate-700">Finish outreach setup</span>
            <span class="block text-[10px] text-slate-500">{{ completedCount }} of {{ items.length }} complete</span>
        </span>
        <UIcon name="i-ion-chevron-up" class="h-3 w-3 flex-none text-slate-400" />
    </button>
</template>

<script setup lang="ts">
type GettingStartedStatus = {
    notifications: boolean;
    exclusionList: boolean;
    firstCampaign: boolean;
};

const props = defineProps<{
    status: GettingStartedStatus;
    variant: 'expanded' | 'collapsed';
}>();

const expanded = useState('getting-started-expanded', () => true);
const newCampaignPath = `/chat/${generateUUID()}?page=new-campaign`;
const items = computed(() => [
    {
        key: 'notifications',
        step: 1,
        label: 'Set up notifications',
        description: 'Choose where replies and critical sending alerts go.',
        to: '/settings/notifications',
        complete: props.status.notifications,
    },
    {
        key: 'exclusionList',
        step: 2,
        label: 'Add a do-not-contact list',
        description: 'Exclude customers, partners, and anyone you should not contact.',
        to: '/settings/forbidden-accounts',
        complete: props.status.exclusionList,
    },
    {
        key: 'firstCampaign',
        step: 3,
        label: 'Build your first campaign',
        description: 'Choose an audience and draft the message you want to send.',
        to: props.status.firstCampaign ? '/campaigns' : newCampaignPath,
        complete: props.status.firstCampaign,
    },
]);
const completedCount = computed(() => items.value.filter(item => item.complete).length);
const progressPercent = computed(() => completedCount.value / items.value.length * 100);
</script>
